import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageSEO from '@/hooks/usePageSEO';
import { siteInfo, faqs } from '@/mocks/videos';

export default function About() {
  const location = useLocation();

  usePageSEO({
    title: '关于我们 - 人人影视 | 品牌介绍、用户口碑与联系方式',
    description: `人人影视成立于${siteInfo.foundedYear}年，专注于高清影视在线观看。汇聚${siteInfo.stats.totalVideos}部影视资源，服务${siteInfo.stats.totalUsers}用户。了解我们的品牌故事、核心优势和联系方式。`,
    keywords: '人人影视,人人影视品牌,人人影视介绍,人人影视联系方式,人人影视口碑,高清免费在线观看平台',
    canonicalPath: '/about',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': '关于我们 - 人人影视',
      'description': `人人影视成立于${siteInfo.foundedYear}年，专注于高清影视在线观看。`,
      'url': 'https://www.renrentv.com/about',
      'isPartOf': {
        '@type': 'WebSite',
        'name': '人人影视',
        'url': 'https://www.renrentv.com/',
      },
      'breadcrumb': {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.renrentv.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': '关于我们' },
        ],
      },
      'mainEntity': {
        '@type': 'FAQPage',
        'mainEntity': faqs.map((faq) => ({
          '@type': 'Question',
          'name': faq.question,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': faq.answer,
          },
        })),
      },
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  关于人人影视
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  人人影视成立于{siteInfo.foundedYear}年，是一家专注于高清影视在线观看的综合性平台。
                  我们始终秉持"高清免费、秒播不缓冲"的服务理念，为广大影迷提供最优质的观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  经过多年发展，人人影视已经汇聚了超过{siteInfo.stats.totalVideos}部影视资源，
                  涵盖电视剧、电影、动漫、综艺、纪录片等全品类内容。平台日活跃用户超过{siteInfo.stats.dailyActive}，
                  累计服务用户突破{siteInfo.stats.totalUsers}。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们采用全球CDN加速技术和先进的视频编码方案，在保证高清画质的同时实现秒播不缓冲。
                  所有资源完全免费，无需会员、无需付费，让每一位用户都能畅享精彩影视内容。
                </p>
              </div>
              <div className="flex-shrink-0 w-full sm:w-80 lg:w-96">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20media%20company%20office%20interior%20with%20large%20screen%20displaying%20film%20content%2C%20warm%20ambient%20lighting%2C%20creative%20workspace%20design%2C%20clean%20professional%20atmosphere%2C%20comfortable%20collaborative%20environment&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="人人影视品牌形象"
                    className="w-full h-full object-cover"
                    title="人人影视品牌形象"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">品牌数据</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{siteInfo.stats.totalVideos}</div>
                <p className="text-xs text-foreground-400 mt-2">影视资源</p>
              </div>
              <div className="text-center p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{siteInfo.stats.dailyActive}</div>
                <p className="text-xs text-foreground-400 mt-2">日活用户</p>
              </div>
              <div className="text-center p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{siteInfo.stats.totalUsers}</div>
                <p className="text-xs text-foreground-400 mt-2">累计用户</p>
              </div>
              <div className="text-center p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{siteInfo.stats.satisfaction}</div>
                <p className="text-xs text-foreground-400 mt-2">用户满意度</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">核心优势</h2>
              <p className="text-sm text-foreground-500">我们用心做好每一个细节</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {siteInfo.features.map((feature, i) => (
                <div key={i} className="p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/70 group hover:border-primary-200 transition-all">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-500 transition-colors mb-4">
                    <i className={`${feature.icon} text-xl text-primary-600 group-hover:text-background-50 transition-colors`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500">听听用户们怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {siteInfo.testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }, (_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们会在24小时内回复。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服电话</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  id="contact-form"
                  data-readdy-form
                  method="POST"
                  action="https://readdy.ai/api/form/d9n3m2cit35ii3jdlo40"
                  encType="application/x-www-form-urlencoded"
                  className="p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/70"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const honeypot = form.querySelector('input[name="phone_alt"]') as HTMLInputElement;
                    if (honeypot && honeypot.value.trim()) {
                      const successMsg = document.getElementById('form-success') as HTMLElement | null;
                      if (successMsg) successMsg.classList.remove('hidden');
                      return;
                    }
                    const formData = new FormData(form);
                    const action = form.getAttribute('action') || '';
                    try {
                      const res = await fetch(action, { method: 'POST', body: formData });
                      const responseText = await res.text();
                      let serverMsg = '';
                      try {
                        const parsed = JSON.parse(responseText);
                        serverMsg = parsed?.meta?.message || parsed?.message || '';
                      } catch { /* ignore */ }
                      const isSpam = responseText.includes('spam') || serverMsg.includes('spam');
                      if (res.ok && !isSpam) {
                        const successMsg = document.getElementById('form-success') as HTMLElement | null;
                        const errorMsg = document.getElementById('form-error') as HTMLElement | null;
                        if (successMsg) successMsg.classList.remove('hidden');
                        if (errorMsg) errorMsg.classList.add('hidden');
                        form.reset();
                      } else {
                        const errorMsg = document.getElementById('form-error') as HTMLElement | null;
                        if (errorMsg) {
                          errorMsg.textContent = serverMsg || '提交失败，请稍后重试';
                          errorMsg.classList.remove('hidden');
                        }
                      }
                    } catch {
                      const errorMsg = document.getElementById('form-error') as HTMLElement | null;
                      if (errorMsg) {
                        errorMsg.textContent = '网络错误，请稍后重试';
                        errorMsg.classList.remove('hidden');
                      }
                    }
                  }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 
                          text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 
                          text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 
                        text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-4 hp-field-wrap">
                    <label htmlFor="hp-phone">Phone</label>
                    <input type="text" id="hp-phone" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200/70 
                        text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50 resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>
                  <div id="form-success" className="hidden mb-4 p-3 rounded-xl bg-green-50 text-green-700 text-sm">
                    提交成功！我们会尽快与您联系。
                  </div>
                  <div id="form-error" className="hidden mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm"></div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium 
                      hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">常见问题</h2>
              <p className="text-sm text-foreground-500">快速解答您的疑问</p>
            </div>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-background-100 
                      group-open:bg-primary-100 transition-colors">
                      <i className="ri-add-line text-sm text-foreground-500 group-open:hidden"></i>
                      <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}